import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, of, startWith, switchMap } from 'rxjs';
import { AmpelBadgeComponent, AmpelState } from '../../../components/ampel-badge/ampel-badge.component';
import { ResPillComponent } from '../../../components/res-pill/res-pill.component';
import { DefectRefChipsComponent } from '../../../components/defect-ref-chips/defect-ref-chips.component';
import { ReleaseReportService } from '../../../services/release-report.service';
import { ReleaseResultsService } from '../../../services/release-results.service';
import { ReleaseBlocker, ReleasePlanResult } from '../../../models/release-results.model';
import { saveBlob } from '../../../shared/file-download';
import {
  ReleaseReportPreview,
  ReportAbschluss,
  ReportAmpel,
  ReportArt,
  ReportFormat,
  ReleaseReportRequest,
  ReportTraceabilityRow,
  ReportUmfang,
} from '../../../models/release-report.model';
import { PlanCaseResult, planCaseResult } from '../plan-ergebnis-tabelle/plan-ergebnis-tabelle.component';

interface PreviewState {
  loading: boolean;
  error: string | null;
  data: ReleaseReportPreview | null;
}

interface PreviewRequest {
  visible: boolean;
  projectId: string;
  releaseId: string;
  art: ReportArt;
  umfang: ReportUmfang;
  planId: string | null;
}

interface PlansState {
  status: 'loading' | 'error' | 'loaded';
  plans: ReleasePlanResult[];
}

interface PlansRequest {
  visible: boolean;
  projectId: string;
  releaseId: string;
}

const AMPEL_STATE: Record<ReportAmpel, AmpelState> = {
  FREIGABEFAEHIG: 'freigabefaehig',
  NOCH_OFFEN: 'noch-offen',
  NICHT_FREIGABEFAEHIG: 'nicht-freigabefaehig',
};

const IDLE: PreviewState = { loading: false, error: null, data: null };
const PLANS_LOADING: PlansState = { status: 'loading', plans: [] };

/**
 * Report erzeugen (page-release-freigabe.jsx:90-198). Eigenes Overlay statt `lib-modal`, weil
 * die Library-Version weder Fokus-Falle noch Escape noch die Maße des Designs bietet.
 */
@Component({
  selector: 'app-report-modal',
  imports: [
    A11yModule,
    DatePipe,
    MatIcon,
    TranslatePipe,
    AmpelBadgeComponent,
    ResPillComponent,
    DefectRefChipsComponent,
  ],
  templateUrl: './report-modal.component.html',
  styleUrl: './report-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportModalComponent {
  private readonly reportService = inject(ReleaseReportService);
  private readonly resultsService = inject(ReleaseResultsService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly releaseId = input.required<string>();
  readonly releaseLabel = input.required<string>();
  readonly visible = model(false);

  protected readonly arts: ReportArt[] = ['TRACEABILITY', 'ABSCHLUSS'];
  protected readonly formats: ReportFormat[] = ['PDF', 'XLSX'];

  protected readonly art = signal<ReportArt>('TRACEABILITY');
  protected readonly umfang = signal<ReportUmfang>('RELEASE');
  protected readonly format = signal<ReportFormat>('PDF');
  private readonly chosenPlanId = signal<string | null>(null);

  private readonly plansState = signal<PlansState>(PLANS_LOADING);
  protected readonly plans = computed(() => this.plansState().plans);
  protected readonly preview = signal<PreviewState>(IDLE);

  protected readonly selectedPlan = computed(() => {
    const plans = this.plans();
    return plans.find((p) => p.id === this.chosenPlanId()) ?? plans[0] ?? null;
  });
  private readonly selectedPlanId = computed(() =>
    this.umfang() === 'PLAN' ? (this.selectedPlan()?.id ?? null) : null,
  );

  protected readonly data = computed(() => this.preview().data);
  protected readonly planCount = computed(() => {
    const { status, plans } = this.plansState();
    return status === 'loaded' ? plans.length : null;
  });
  private readonly planStatusInScope = computed(() => (this.umfang() === 'PLAN' ? this.plansState().status : null));
  protected readonly loading = computed(() => this.preview().loading || this.planStatusInScope() === 'loading');
  protected readonly plansFailed = computed(() => this.planStatusInScope() === 'error');
  protected readonly isEmpty = computed(() => {
    const d = this.data();
    if (!d) return false;
    if (d.art === 'TRACEABILITY') return (d.traceability ?? []).length === 0;
    return !d.abschluss || (d.abschluss.gesamt === 0 && d.abschluss.traceability.length === 0);
  });
  protected readonly generating = signal(false);
  protected readonly generateError = signal<string | null>(null);
  protected readonly confirmation = signal<string | null>(null);
  protected readonly canGenerate = computed(
    () => !this.generating() && !this.loading() && (this.umfang() === 'RELEASE' || this.selectedPlan() !== null),
  );

  protected readonly ampel = computed<AmpelState | null>(() => {
    const a = this.data()?.abschluss;
    return a ? AMPEL_STATE[a.ampel] : null;
  });

  private readonly previewRequest = computed<PreviewRequest>(() => ({
    visible: this.visible(),
    projectId: this.projectId(),
    releaseId: this.releaseId(),
    art: this.art(),
    umfang: this.umfang(),
    planId: this.selectedPlanId(),
  }));

  private readonly plansRequest = computed<PlansRequest>(() => ({
    visible: this.visible(),
    projectId: this.projectId(),
    releaseId: this.releaseId(),
  }));

  private triggerElement: HTMLElement | null = null;

  constructor() {
    let wasVisible = false;
    effect(() => {
      const open = this.visible();
      if (open && !wasVisible) {
        this.triggerElement = document.activeElement as HTMLElement | null;
        document.body.style.overflow = 'hidden';
      } else if (!open && wasVisible) {
        this.generateError.set(null);
        this.confirmation.set(null);
        document.body.style.overflow = '';
        this.triggerElement?.focus();
        this.triggerElement = null;
      }
      wasVisible = open;
    });
    this.destroyRef.onDestroy(() => (document.body.style.overflow = ''));

    toObservable(this.plansRequest)
      .pipe(
        switchMap((req) =>
          req.visible
            ? this.resultsService.getPlans(req.projectId, req.releaseId, {}).pipe(
                map((plans): PlansState => ({ status: 'loaded', plans })),
                catchError(() => of<PlansState>({ status: 'error', plans: [] })),
                startWith(PLANS_LOADING),
              )
            : of(null),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        if (state) this.plansState.set(state);
      });

    toObservable(this.previewRequest)
      .pipe(
        switchMap((req) => {
          if (!req.visible || (req.umfang === 'PLAN' && !req.planId)) return of<PreviewState>(IDLE);
          const previous = this.preview().data;
          return this.reportService.getPreview(req.projectId, req.releaseId, req.art, req.planId).pipe(
            map((data): PreviewState => ({ loading: false, error: null, data })),
            catchError((err: unknown) =>
              of<PreviewState>({ loading: false, error: this.messageFor(err, 'preview'), data: null }),
            ),
            startWith<PreviewState>({ loading: true, error: null, data: previous }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => this.preview.set(state));
  }

  protected generate(): void {
    if (!this.canGenerate()) return;
    const plan = this.umfang() === 'PLAN' ? this.selectedPlan() : null;
    const request: ReleaseReportRequest = {
      art: this.art(),
      umfang: this.umfang(),
      ...(plan ? { planId: plan.id } : {}),
      format: this.format(),
    };
    const confirmation = this.translate.instant('testResults.report.done', {
      art: this.translate.instant(`testResults.report.art.${request.art}.title`),
      scope: plan ? (plan.key ?? plan.titel) : this.releaseLabel(),
      format: request.format,
    });
    this.generating.set(true);
    this.generateError.set(null);
    this.confirmation.set(null);
    this.reportService
      .generate(this.projectId(), this.releaseId(), request, plan?.key ?? this.releaseId())
      .pipe(
        finalize(() => this.generating.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ blob, filename }) => {
          saveBlob(blob, filename);
          this.confirmation.set(confirmation);
        },
        error: (err: unknown) => this.generateError.set(this.messageFor(err, 'generate')),
      });
  }

  protected choosePlan(id: string): void {
    this.chosenPlanId.set(id);
  }

  protected planLabel(plan: ReleasePlanResult): string {
    return plan.key ? `${plan.key} · ${plan.titel}` : plan.titel;
  }

  protected scopeLabel(preview: ReleaseReportPreview): string {
    return preview.plan ? (preview.plan.key ?? preview.plan.titel) : this.releaseLabel();
  }

  protected resultOf(row: ReportTraceabilityRow): PlanCaseResult | null {
    return row.status ? planCaseResult(row.status) : null;
  }

  protected casesWithDefects(abschluss: ReportAbschluss): ReleaseBlocker[] | null {
    const cases = abschluss.offeneDefects.filter((c) => c.defects.length > 0);
    return cases.length > 0 ? cases : null;
  }

  protected close(): void {
    this.visible.set(false);
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.close();
  }

  private messageFor(err: unknown, kind: 'preview' | 'generate'): string {
    if (err instanceof HttpErrorResponse && err.status === 403) {
      return this.translate.instant('testResults.report.errors.forbidden');
    }
    const serverMessage = err instanceof HttpErrorResponse ? err.error?.error : null;
    return typeof serverMessage === 'string' && serverMessage.length > 0
      ? serverMessage
      : this.translate.instant(`testResults.report.errors.${kind}`);
  }
}
